import React, { useState, useEffect, useCallback } from 'react';
import { Users, Bot, Building2, RefreshCw, Check, AlertCircle, User, Trash2 } from 'lucide-react';
import { neonQuery } from '../services/api';
import WorkItemDetail from '../components/WorkItemDetail';

// One view over every open work item in the org - Seventh Street, accounting,
// marketing, tickets and Greg's own board - with a single owner vocabulary so
// "what is Mercedes carrying" is answerable for the first time.

const HORIZONS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'quarter', label: 'Next 3 months' },
  { key: 'someday', label: 'Someday' },
];

const KIND_META = {
  person: { icon: User, color: 'var(--navy)', bg: '#eef2f6', label: 'On staff' },
  agent: { icon: Bot, color: '#6b21a8', bg: '#f5edff', label: 'Agent' },
  external: { icon: Building2, color: '#8a5a00', bg: '#fff4dd', label: 'Outside' },
  unassigned: { icon: AlertCircle, color: '#a12020', bg: '#fdecec', label: 'Needs a decision' },
};
const kindMeta = (k) => KIND_META[k] || KIND_META.unassigned;

// Only Greg's board carries owner_source / horizon, so only it is editable here.
const EDITABLE = new Set(['greg_weekly_tasks', 'greg_backlog', 'greg_project_tasks']);

export default function Delegation() {
  const [summary, setSummary] = useState([]);
  const [pending, setPending] = useState(0);
  const [owners, setOwners] = useState([]);
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);          // owner_key, or '__none__'
  const [stream, setStream] = useState('business');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [tick, setTick] = useState(0);
  const [detail, setDetail] = useState(null);
  const [picked, setPicked] = useState(() => new Set());

  const load = useCallback(async () => {
    try {
      const [s, o] = await Promise.all([
        neonQuery('work_summary', {}),
        neonQuery('work_owners_list', {}),
      ]);
      setSummary(s.summary || []);
      setPending(s.pending || 0);
      setOwners(o.owners || []);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // One effect owns the open list, keyed on the selection, the stream toggle and
  // a tick that writes bump - so a write refreshes without a second fetch path.
  useEffect(() => {
    if (!sel) { setItems([]); return; }
    let live = true;
    setBusy(true);
    neonQuery('work_open_list',
      sel === '__none__' ? { unassignedOnly: true, stream } : { ownerKey: sel, stream })
      .then((rows) => { if (live) { setItems(Array.isArray(rows) ? rows : []); setPicked(new Set()); } })
      .catch((e) => { if (live) setErr(e.message); })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [sel, stream, tick]);

  const setOwner = async (row, ownerKey) => {
    setBusy(true);
    try {
      await neonQuery('work_set_owner', { srcTable: row.src_table, srcId: row.src_id, ownerKey });
      await load();
      setTick((t) => t + 1);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const setHorizon = async (row, horizon) => {
    setBusy(true);
    try {
      await neonQuery('work_set_horizon', { srcTable: row.src_table, srcId: row.src_id, horizon });
      setTick((t) => t + 1);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const deleteItem = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?

This removes it from your board for good.`)) return;
    setBusy(true);
    try {
      await neonQuery('work_delete_item', { srcTable: row.src_table, srcId: row.src_id });
      await load();
      setTick((t) => t + 1);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const deleteSuperseded = async () => {
    if (!window.confirm(`Delete every item marked "superseded"?

These are rows replaced by something else - the countdown steps folded into "Clear email inbox", for instance.`)) return;
    setBusy(true);
    try {
      const r = await neonQuery('work_delete_superseded', {});
      await load();
      setTick((t) => t + 1);
      setErr(r && r.deleted ? '' : 'Nothing was marked superseded.');
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const rowKey = (r) => `${r.src_table}|${r.src_id}`;
  const pickedRows = () => items.filter((r) => picked.has(rowKey(r)));
  const togglePick = (r) => setPicked((prev) => {
    const next = new Set(prev);
    const k = rowKey(r);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const toggleAll = () => setPicked((prev) => (
    prev.size === items.length ? new Set() : new Set(items.map(rowKey))
  ));

  const deleteSelected = async () => {
    const rows = pickedRows();
    if (!rows.length) return;
    if (!window.confirm(`Delete ${rows.length} item${rows.length > 1 ? 's' : ''}?

This removes them from your board for good.`)) return;
    setBusy(true);
    try {
      const r = await neonQuery('work_delete_many', {
        items: rows.map((x) => ({ srcTable: x.src_table, srcId: x.src_id })),
      });
      if (r && r.skipped) setErr(`${r.skipped} item(s) belong to another brain and were left alone.`);
      await load();
      setTick((t) => t + 1);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const assignSelected = async (patch) => {
    const rows = pickedRows();
    if (!rows.length) return;
    setBusy(true);
    try {
      await neonQuery('work_assign_many', {
        items: rows.map((x) => ({ srcTable: x.src_table, srcId: x.src_id })),
        ...patch,
      });
      await load();
      setTick((t) => t + 1);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const confirmAll = async () => {
    if (!window.confirm(`Accept all ${pending} suggested owners? You can still change any of them afterwards.`)) return;
    setBusy(true);
    try {
      await neonQuery('work_confirm_all', {});
      await load();
      setTick((t) => t + 1);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="page"><div style={{ padding: 40, color: '#8b94a3' }}>Loading work…</div></div>;
  }

  const supersededCount = items.filter((i) => (i.triage_note || '').startsWith('Superseded by')).length;
  const totalBiz = summary.reduce((a, r) => a + Number(r.business || 0), 0);
  const totalPers = summary.reduce((a, r) => a + Number(r.personal || 0), 0);

  return (
    <div className="page">
      <div className="breadcrumb">
        <a href="#todo">To-Do</a><span> / </span><span>Delegation</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap', marginBottom: 4 }}>
        <h1 style={{ color: 'var(--navy)', margin: 0 }}>Delegation</h1>
        <span style={{ color: '#8b94a3', fontSize: 13 }}>
          {totalBiz} open business items · {totalPers} personal · across every brain and your own board
        </span>
      </div>

      {err && (
        <div style={{ background: '#fdecec', color: '#a12020', padding: '8px 12px', borderRadius: 6, margin: '10px 0', fontSize: 13 }}>
          {err}
        </div>
      )}

      {pending > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          background: '#fff4dd', border: '1px solid #f0d9a8', borderRadius: 6,
          padding: '10px 14px', margin: '12px 0',
        }}>
          <AlertCircle size={16} color="#8a5a00" />
          <span style={{ flex: 1, minWidth: 240, fontSize: 13, color: '#5a4a20' }}>
            <strong>{pending} suggested owners</strong> are waiting on you. Nothing has been assigned
            for real yet — open an owner below to review, or accept them all and adjust as you go.
          </span>
          <button className="btn btn-primary" onClick={confirmAll} disabled={busy}>
            Accept all {pending}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, margin: '14px 0' }}>
        {['business', 'personal'].map((s) => (
          <button key={s} onClick={() => setStream(s)} className={stream === s ? 'btn btn-navy' : 'btn btn-outline'}
            style={{ textTransform: 'capitalize' }}>
            {s}
          </button>
        ))}
        <button className="btn btn-ghost" onClick={load} disabled={busy} title="Refresh">
          <RefreshCw size={14} />
        </button>
        {supersededCount > 0 && (
          <button className="btn btn-outline" onClick={deleteSuperseded} disabled={busy}
            title="Remove rows that were replaced by something else"
            style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Trash2 size={14} /> Clear {supersededCount} superseded
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 340px) 1fr', gap: 20, alignItems: 'start' }}>
        {/* Who is carrying what */}
        <div className="card">
          <div className="card-header"><Users size={15} /> Who is carrying what</div>
          <div className="card-body" style={{ padding: 0 }}>
            {summary
              .filter((r) => Number(stream === 'business' ? r.business : r.personal) > 0)
              .map((r) => {
                const m = kindMeta(r.owner_key ? r.owner_kind : 'unassigned');
                const Icon = m.icon;
                const n = Number(stream === 'business' ? r.business : r.personal);
                const key = r.owner_key || '__none__';
                const active = sel === key;
                return (
                  <button key={key} onClick={() => setSel(key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                      padding: '10px 14px', border: 'none', cursor: 'pointer', textAlign: 'left',
                      background: active ? '#eef2f6' : 'transparent',
                      borderLeft: `3px solid ${active ? 'var(--orange)' : 'transparent'}`,
                      borderBottom: '1px solid #eef1f4',
                    }}>
                    <span style={{ background: m.bg, color: m.color, borderRadius: 6, padding: 5, display: 'flex' }}>
                      <Icon size={14} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontWeight: 600, color: 'var(--navy)' }}>{r.owner_name}</span>
                      <span style={{ fontSize: 11, color: '#8b94a3' }}>{m.label}</span>
                    </span>
                    <span style={{ fontWeight: 700, color: 'var(--navy)' }}>{n}</span>
                  </button>
                );
              })}
          </div>
        </div>

        {/* The items */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {sel && items.length > 0 && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400, cursor: 'pointer' }}>
                <input type="checkbox" checked={picked.size === items.length && items.length > 0}
                  onChange={toggleAll} style={{ cursor: 'pointer' }} />
                <span style={{ fontSize: 12, color: '#5a6272' }}>All</span>
              </label>
            )}
            <span>
              {sel ? `${(summary.find((r) => (r.owner_key || '__none__') === sel) || {}).owner_name || 'Items'} — ${items.length} open` : 'Pick someone on the left'}
            </span>
          </div>

          {picked.size > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              padding: '10px 14px', background: '#eef2f6', borderBottom: '1px solid #dbe3ea',
            }}>
              <strong style={{ color: 'var(--navy)', fontSize: 13 }}>{picked.size} selected</strong>
              <select className="form-select" value="" disabled={busy}
                onChange={(e) => { if (e.target.value) assignSelected({ ownerKey: e.target.value === '__none__' ? null : e.target.value }); }}
                style={{ maxWidth: 175, fontSize: 12 }}>
                <option value="">Assign to…</option>
                <option value="__none__">— unassign —</option>
                {owners.map((o) => <option key={o.owner_key} value={o.owner_key}>{o.display_name}</option>)}
              </select>
              <select className="form-select" value="" disabled={busy}
                onChange={(e) => { if (e.target.value) assignSelected({ horizon: e.target.value === '__none__' ? null : e.target.value }); }}
                style={{ maxWidth: 150, fontSize: 12 }}>
                <option value="">Set when…</option>
                <option value="__none__">— clear —</option>
                {HORIZONS.map((h) => <option key={h.key} value={h.key}>{h.label}</option>)}
              </select>
              <button className="btn btn-ghost" onClick={() => setPicked(new Set())} disabled={busy}
                style={{ fontSize: 12 }}>Clear</button>
              <button className="btn btn-danger" onClick={deleteSelected} disabled={busy}
                style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Trash2 size={14} /> Delete {picked.size}
              </button>
            </div>
          )}
          <div className="card-body">
            {!sel && (
              <div style={{ color: '#8b94a3', fontSize: 13, padding: '8px 0' }}>
                Every open item in the org, grouped by who it belongs to. Items on your own board
                can be reassigned here; items already owned inside another brain are shown for load
                but are edited in that brain.
              </div>
            )}
            {sel && busy && <div style={{ color: '#8b94a3', fontSize: 13 }}>Working…</div>}
            {sel && !busy && !items.length && (
              <div style={{ color: '#8b94a3', fontSize: 13 }}>Nothing open here.</div>
            )}
            {sel && !busy && items.map((row) => {
              const editable = EDITABLE.has(row.src_table);
              const suggested = row.owner_source === 'suggested';
              return (
                <div key={`${row.src_table}-${row.src_id}`}
                  style={{
                    padding: '11px 0', borderBottom: '1px solid #eef1f4',
                    background: picked.has(rowKey(row)) ? '#f7fafc' : 'transparent',
                  }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    {editable && (
                      <input type="checkbox" checked={picked.has(rowKey(row))}
                        onChange={() => togglePick(row)}
                        style={{ marginTop: 4, cursor: 'pointer', flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <button onClick={() => setDetail(row)} title="Add context, a screenshot, or dictate a note"
                        style={{
                          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                          textAlign: 'left', fontWeight: 600, color: 'var(--navy)', fontSize: 'inherit',
                          fontFamily: 'inherit', textDecoration: 'underline', textDecorationColor: '#d6dde5',
                          textUnderlineOffset: 3,
                        }}>{row.title}</button>
                      <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: '#8b94a3' }}>{row.domain}</span>
                        {row.due_date && (
                          <span style={{ fontSize: 11, color: '#5a6272' }}>due {row.due_date}</span>
                        )}
                        {suggested && (
                          <span style={{
                            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
                            background: '#fff4dd', color: '#8a5a00', borderRadius: 20, padding: '2px 8px',
                          }}>suggested</span>
                        )}
                        {Number(row.child_count) > 0 && (
                          <span style={{
                            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
                            background: '#eef2f6', color: 'var(--navy)', borderRadius: 20, padding: '2px 8px',
                          }}>{row.child_count} pieces</span>
                        )}
                      </div>
                      {row.triage_note && (
                        <div style={{ fontSize: 12, color: '#5a6272', marginTop: 5, lineHeight: 1.45 }}>
                          {row.triage_note}
                        </div>
                      )}
                    </div>
                    {editable ? (
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <select className="form-select" value={row.owner_key || ''} disabled={busy}
                          onChange={(e) => setOwner(row, e.target.value)}
                          style={{ maxWidth: 165, fontSize: 12, padding: '3px 6px' }}>
                          <option value="">— unassigned —</option>
                          {owners.map((o) => (
                            <option key={o.owner_key} value={o.owner_key}>{o.display_name}</option>
                          ))}
                        </select>
                        {row.src_table !== 'greg_project_tasks' && (
                          <select className="form-select" value={row.horizon || ''} disabled={busy}
                            onChange={(e) => setHorizon(row, e.target.value)}
                            style={{ maxWidth: 130, fontSize: 12, padding: '3px 6px' }}>
                            <option value="">— when? —</option>
                            {HORIZONS.map((h) => <option key={h.key} value={h.key}>{h.label}</option>)}
                          </select>
                        )}
                        {suggested && (
                          <button className="btn btn-ghost" disabled={busy} title="Accept this suggestion"
                            onClick={() => setOwner(row, row.owner_key)}
                            style={{ padding: '3px 8px' }}>
                            <Check size={13} />
                          </button>
                        )}
                        <button className="btn btn-ghost" disabled={busy} title="Delete this item"
                          onClick={() => deleteItem(row)}
                          style={{ padding: '3px 8px', color: '#b9c0cb' }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ) : (
                      <span style={{ fontSize: 11, color: '#b9c0cb', flexShrink: 0 }}>
                        edited in {row.domain}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {detail && (
        <WorkItemDetail item={detail} onClose={() => setDetail(null)}
          onSaved={() => setTick((t) => t + 1)} />
      )}
    </div>
  );
}
